import {
  UserOutlined,
  DollarOutlined,
  ProfileOutlined,
  CheckSquareOutlined,
  SettingOutlined,
  QuestionCircleOutlined,
  OrderedListOutlined,
  PictureOutlined,
  UsergroupDeleteOutlined,
  BarChartOutlined,
  NotificationOutlined,
  FileExcelOutlined,
  BarsOutlined,
  ContainerOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { ReactNode } from "react";

import { Role } from "@/graphql";
import { PageKey, rolesWithView } from "@/utils/permissions";

export type CategoryItem = {
  key: string;
  name: string;
  path: string;
  role: Role[];
  icon?: ReactNode;
  pageKey?: PageKey;
  subCategories?: CategoryItem[];
};

const sub = (
  key: string,
  name: string,
  path: string,
  pageKey: PageKey
): CategoryItem => ({
  key,
  name,
  path,
  pageKey,
  role: rolesWithView(pageKey),
});

const parentRoles = (children: CategoryItem[]): Role[] => {
  const set = new Set<Role>();
  for (const child of children) {
    for (const r of child.role) set.add(r);
  }
  return Array.from(set);
};

const dashboardItem: CategoryItem = {
  key: "parent1",
  name: "Хяналтын самбар",
  path: "/",
  pageKey: "dashboard",
  role: rolesWithView("dashboard"),
  icon: <BarChartOutlined />,
};

const customers: CategoryItem = (() => {
  const subs = [
    sub("users-list", "Харилцагчдын мэдээлэл", "/users", "users-list"),
    {
      ...sub(
        "parent9",
        "Хэрэглэгч устгах хүсэлт",
        "/userdeletionrequests",
        "user-deletion-request"
      ),
      icon: <UsergroupDeleteOutlined />,
    },
  ];
  return {
    key: "customers",
    name: "Харилцагчид",
    path: "/users",
    role: parentRoles(subs),
    icon: <UserOutlined />,
    subCategories: subs,
  };
})();

const verification: CategoryItem = (() => {
  const subs = [
    {
      ...sub("parent5", "KYC", "/kycs", "kyc"),
      icon: <CheckSquareOutlined />,
    },
    sub("contracts", "Гэрээ", "/contracts", "contracts"),
    sub("mscd-accounts", "Үнэт цаасны данс", "/mscdaccounts", "mscd-accounts"),
    sub(
      "kyc-questionnaire",
      "KYC Судалгаа",
      "/kyc-questionnaires",
      "kyc-questionnaire"
    ),
  ];
  return {
    key: "user-verification",
    name: "Баталгаажуулалт",
    path: "/kycs",
    role: parentRoles(subs),
    icon: <CheckSquareOutlined />,
    subCategories: subs,
  };
})();

const portfolio: CategoryItem = (() => {
  const subs = [
    sub("sub3", "Бонд", "/bonds", "bonds"),
    sub("sub4", "Үнэт цаас", "/stocks", "stocks"),
    sub("sub5", "Үл хөдлөх хөрөнгө", "/savings", "savings"),
    sub("sub6", "Багцын задаргаа", "/portfolios", "portfolios"),
  ];
  return {
    key: "parent2",
    name: "Багц",
    path: "/portfolio",
    role: parentRoles(subs),
    icon: <OrderedListOutlined />,
    subCategories: subs,
  };
})();

const investments: CategoryItem = (() => {
  const subs = [
    sub("sub7", "Цэнэглэлт", "/orders", "orders"),
    sub("sub8", "Захиалгын хуудас", "/orderlists", "orderlists"),
    sub("sub9", "Данс", "/balances", "balances"),
    sub("huulga", "Хуулга", "/huulga", "huulga"),
    sub(
      "dividends-stock",
      "Хувьцааны ноогдол ашиг",
      "/stocks/dividends",
      "stock-dividends"
    ),
    sub(
      "unmatched-deposits",
      "Танигдаагүй гүйлгээ",
      "/unmatched-deposits",
      "unmatched-deposits"
    ),
  ];
  return {
    key: "parent3",
    name: "Хөрөнгө оруулалт",
    path: "/investments",
    role: parentRoles(subs),
    icon: <DollarOutlined />,
    subCategories: subs,
  };
})();

const refund: CategoryItem = (() => {
  const subs = [
    sub("refundPage", "Буцаалтын захиалга", "/refund", "refund-orders"),
    sub("refundList", "Буцаалтын жагсаалт", "/refund/list", "refund-list"),
    sub(
      "refundCalculation",
      "Буцаалтын тооцоолол",
      "/refund/calculation",
      "refund-calculation"
    ),
  ];
  return {
    key: "refund",
    name: "Буцаалт",
    path: "/refund",
    role: parentRoles(subs),
    icon: <BarsOutlined />,
    subCategories: subs,
  };
})();

const reports: CategoryItem = (() => {
  const subs = [
    sub(
      "interest-payout",
      "Хүү олголт",
      "/reports/interest-payout",
      "reports-interest"
    ),
    sub("fee", "Шимтгэл", "/reports/fee", "reports-fee"),
    sub("monthly-fee", "Сарын хураамж", "/monthlyfee", "reports-monthly-fee"),
    sub(
      "corrections",
      "Залруулга",
      "/reports/corrections",
      "reports-corrections"
    ),
    sub(
      "balance-unhold",
      "Битүүмж чөлөөлөлт",
      "/reports/hold-release",
      "reports-hold-release"
    ),
  ];
  return {
    key: "reports",
    name: "Тайлан",
    path: "/reports",
    role: parentRoles(subs),
    icon: <FileExcelOutlined />,
    subCategories: subs,
  };
})();

const other: CategoryItem = (() => {
  const subs = [
    {
      ...sub("parent4", "Судалгаа", "/surveys", "surveys"),
      icon: <ProfileOutlined />,
    },
    {
      ...sub("parent6", "FAQ", "/faqs", "faq"),
      icon: <QuestionCircleOutlined />,
    },
    {
      ...sub("parent7", "Тохиргоо", "/configs", "configs"),
      icon: <SettingOutlined />,
    },
    {
      ...sub("parent8", "Зурагнууд", "/images", "images"),
      icon: <PictureOutlined />,
    },
    {
      ...sub("parent10", "Мэдэгдэл", "/activity", "activity"),
      icon: <NotificationOutlined />,
    },
    {
      ...sub("parent11", "Зөвлөгөө", "/suggest", "suggest"),
      icon: <ContainerOutlined />,
    },
  ];
  return {
    key: "other",
    name: "Бусад",
    path: "/other",
    role: parentRoles(subs),
    icon: <AppstoreOutlined />,
    subCategories: subs,
  };
})();

export const categories: CategoryItem[] = [
  dashboardItem,
  customers,
  verification,
  portfolio,
  investments,
  refund,
  reports,
  other,
];
